import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import {
  SidePanel,
  SidePanelType,
  ButtonPseudo,
  ButtonPrimary,
  usePrevious,
  Popup,
  SidePanelLoader,
} from '@ui/src';
import { useAppDispatch, useAppSelector } from '@store';
import {
  onSubmitCard,
  clearOrderCardForm,
  getCardInfo,
} from '../store/slices/cards/cards';
import { OrderCardFormValues } from '../../customers/types';
import OrderCardForm from '../../../components/partial/cards/order';

import ROUTES from '../../../routes';

const CustomerOrderCard = () => {
  const [panelIsOpen, setPanelIsOpen] = useState(false);
  const [dirtyPopupVisible, setDirtyPopupVisible] = useState(false);

  const { customerId, cardId } = useParams();
  const navigate = useNavigate();

  const dispatch = useAppDispatch();

  const {
    individualCustomers: {
      info: {
        cards: {
          cardSubmitInProgress,
          cardSubmitSuccess,
          cardInfo,
          cardInfoLoading,
        },
      },
    },
    customersDictionaries: { countries },
  } = useAppSelector((state) => state);

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
    watch,
    reset,
    setValue,
  } = useForm<OrderCardFormValues>();

  const physical = watch('physical');

  const prevPhysical = usePrevious(physical);

  const onSubmit = handleSubmit((values) => {
    dispatch(onSubmitCard(values));
  });

  const handleClose = () => {
    setPanelIsOpen(false);
    setDirtyPopupVisible(false);
    reset({});

    setTimeout(() => {
      dispatch(clearOrderCardForm());

      navigate(
        generatePath(ROUTES.CUSTOMERS_INDIVIDUAL_CARDS, {
          customerId: customerId as string,
        }),
      );
    }, 100);
  };

  useEffect(() => {
    setPanelIsOpen(true);

    if (customerId) {
      reset({
        userId: customerId,
      });
    }

    if (cardId) {
      dispatch(getCardInfo(cardId));
    }
  }, [dispatch, customerId, cardId]);

  useEffect(() => {
    if (cardSubmitSuccess) {
      handleClose();
    }
  }, [cardSubmitSuccess]);

  useEffect(() => {
    if (cardInfo && countries && customerId) {
      const currentCountry = countries.find(
        (item) => item.value === cardInfo.countryId,
      );

      reset({
        userId: customerId,
        physical: !cardInfo.virtual,
        cardType: cardInfo.virtual ? 'virtual' : 'physical',
        paymentSystem: cardInfo.paymentSystem,
        countryId: cardInfo.countryId || '',
        city: cardInfo.city || '',
        postCode: cardInfo.postCode || '',
        street: cardInfo.street || '',
        apartment: cardInfo.apartment || '',
        countryName: currentCountry ? currentCountry.name : '',
      });
    }
  }, [cardInfo, customerId, countries]);

  useEffect(() => {
    if (!physical && prevPhysical) {
      setValue('countryId', '');
      setValue('city', '');
      setValue('street', '');
      setValue('apartment', '');
      setValue('postCode', '');
    }
  }, [physical, prevPhysical]);

  let content;

  if (!cardInfoLoading) {
    content = (
      <OrderCardForm
        control={control}
        errors={errors}
        physical={physical}
        countries={countries}
        setValue={setValue}
      />
    );
  } else {
    content = <SidePanelLoader />;
  }

  let buttonText;

  if (cardSubmitInProgress) {
    buttonText = 'Loading...';
  } else {
    buttonText = 'Order a card';

    if (cardId) {
      buttonText = 'Save';
    }
  }

  return (
    <>
      <SidePanel
        type={SidePanelType.Cards}
        closeOnClickOutside
        isOpen={panelIsOpen}
        onClose={isDirty ? () => setDirtyPopupVisible(true) : handleClose}
        title={cardId ? 'Edit card' : 'Order a card'}
        sidepanelExpandedKey="customers-card-panel"
        footer={
          <>
            <ButtonPrimary
              text={buttonText}
              onClick={cardId ? handleClose : onSubmit}
              disabled={cardSubmitInProgress}
              bold
            />
            <ButtonPseudo
              text="Close"
              onClick={isDirty ? () => setDirtyPopupVisible(true) : handleClose}
              bold
            />
          </>
        }
      >
        {content}
      </SidePanel>
      <Popup
        title="You have unsaved changes that will be lost"
        description="Are you sure you want to close this form?"
        onCancelClick={() => setDirtyPopupVisible(false)}
        onOkClick={handleClose}
        cancelButtonText="Cancel"
        okButtonText="Close"
        isVisible={dirtyPopupVisible}
        parent={document.getElementById('dirty-popup') as HTMLElement}
      />
      <div id="dirty-popup" />
    </>
  );
};

export default CustomerOrderCard;
